import { supabase } from "@/integrations/supabase/client";

/**
 * Centralized error reporting for critical application errors.
 * Logs to audit_log table with structured context for admin visibility.
 * Never logs PII (email, name, address) — only user_id and technical context.
 */

type ErrorSeverity = "critical" | "high" | "medium" | "low";

interface ErrorContext {
  /** Which feature/flow failed */
  feature: string;
  /** HTTP status or error code if applicable */
  status_code?: number;
  /** Related trip ID */
  trip_id?: string;
  /** Edge function name */
  endpoint?: string;
  /** Additional safe details */
  detail?: string;
}

const recentErrors = new Map<string, number>();
const DEDUP_MS = 10_000; // 10s dedup window

function isDuplicate(key: string): boolean {
  const now = Date.now();
  const last = recentErrors.get(key);
  if (last && now - last < DEDUP_MS) return true;
  recentErrors.set(key, now);
  if (recentErrors.size > 50) {
    for (const [k, v] of recentErrors) {
      if (now - v > DEDUP_MS * 3) recentErrors.delete(k);
    }
  }
  return false;
}

/**
 * Report a critical error to the audit log.
 * Safe to call from anywhere — never throws, never logs PII.
 */
export async function reportError(
  severity: ErrorSeverity,
  message: string,
  context: ErrorContext
) {
  const dedupKey = `${context.feature}:${message}:${context.status_code ?? ""}`;
  if (isDuplicate(dedupKey)) return;

  // Also log to console for dev visibility
  console.error(`[ErrorReport:${severity}] ${context.feature}: ${message}`, context);

  try {
    const { data: { user } } = await supabase.auth.getUser();

    await supabase.rpc("write_audit_log", {
      _user_id: user?.id ?? null,
      _action: `error_${severity}`,
      _resource_type: context.feature,
      _resource_id: context.trip_id ?? context.endpoint ?? null,
      _ip_address: null,
      _details: {
        message,
        status_code: context.status_code,
        endpoint: context.endpoint,
        trip_id: context.trip_id,
        detail: context.detail,
        url: window.location.pathname,
        device: window.innerWidth < 768 ? "mobile" : "desktop",
        timestamp: new Date().toISOString(),
      },
    });
  } catch {
    // Error reporting must never break the app
  }
}

/**
 * Wrap a promise-based operation with automatic error reporting.
 */
export async function withErrorReporting<T>(
  feature: string,
  operation: () => Promise<T>,
  context?: Partial<ErrorContext>
): Promise<T> {
  try {
    return await operation();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    await reportError("high", message, { feature, ...context });
    throw err;
  }
}

/**
 * Global unhandled error/rejection handler.
 * Attach once in main.tsx.
 */
export function installGlobalErrorHandlers() {
  window.addEventListener("error", (event) => {
    reportError("critical", event.message || "Unhandled error", {
      feature: "global_error",
      detail: event.filename ? `${event.filename}:${event.lineno}` : undefined,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const message = event.reason instanceof Error
      ? event.reason.message
      : String(event.reason);
    reportError("critical", message, {
      feature: "unhandled_rejection",
    });
  });
}
